import { useCallback, useEffect } from "react";

import useBasicData from "@/hooks/useBasicData";
import { useFileUpload } from "@/hooks/useFileUpload";
import useShowSnackbar from "@/hooks/useShowSnackbar";
import { validateFile } from "@/utils/fileValidation";
import { convertVideoFileToFilepickerFile } from "@/utils/video";

import { useFilePickerState } from "../state";

interface UsePasteHandlerProps {
  enabled?: boolean;
}

export const useFilePickerPaste = (props: UsePasteHandlerProps): void => {
  const { enabled = true } = props;
  const {
    maxFiles,
    selectedFiles,
    acceptedFileTypes,
    modality,
    setIsUploading,
    setSelectedFiles,
    setSelectionAsConfirmed,
  } = useFilePickerState();

  const { upload } = useFileUpload();
  const { activeWorkspace } = useBasicData();
  const { showError, showWarning } = useShowSnackbar();

  const handlePaste = useCallback(
    async (event: ClipboardEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (!enabled) {
        return;
      }

      const pastedFiles = Array.from(event.clipboardData?.files || []);

      if (pastedFiles.length === 0) {
        showWarning("No files on clipboard.");
        return;
      }

      if (maxFiles && maxFiles < pastedFiles.length) {
        showError(`You can only upload up to ${maxFiles} file(s) at a time.`);
        return;
      }

      const isAllValidFiles = pastedFiles.every((file) =>
        validateFile(file, acceptedFileTypes),
      );

      if (!isAllValidFiles) {
        const mimeTypes = Object.keys(acceptedFileTypes ?? {}).join(", ");
        showError(
          `Invalid file type. You can only paste files of mimetype ${mimeTypes}.`,
        );
        return;
      }

      try {
        setIsUploading(true);

        const uploadPromises = pastedFiles.map(async (file) => {
          const fileType = file.type.split("/")[0];

          // When selecting a video file when the modality is IMAGE, the user will need to select a frame from the video
          if (fileType === "video" && modality === "IMAGE") {
            return convertVideoFileToFilepickerFile(file);
          }

          return upload(file, {
            sourceType: "REFERENCE",
          });
        });

        const uploadedFiles = await Promise.allSettled(uploadPromises);
        const successfulUploads = uploadedFiles
          .filter((result) => result.status === "fulfilled")
          .map((result) => result.value)
          .filter(Boolean);

        if (successfulUploads.length === 0) {
          showError("Failed to upload any files.");
          return;
        }

        setSelectedFiles(successfulUploads);
        setSelectionAsConfirmed();
      } catch (error) {
        console.error("Error uploading pasted files:", error);
        showError("Failed to upload one or more pasted files.");
      } finally {
        setIsUploading(false);
      }
    },
    [
      enabled,
      maxFiles,
      activeWorkspace,
      acceptedFileTypes,
      modality,
      selectedFiles,
      upload,
      setSelectionAsConfirmed,
      showError,
      setIsUploading,
    ],
  );

  useEffect(() => {
    if (enabled) {
      document.addEventListener(
        "paste",
        handlePaste as unknown as EventListener,
      );
    } else {
      document.removeEventListener(
        "paste",
        handlePaste as unknown as EventListener,
      );
    }

    return () => {
      document.removeEventListener(
        "paste",
        handlePaste as unknown as EventListener,
      );
    };
  }, [enabled, handlePaste]);
};
